import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { StyleInfo, styleMap } from 'lit/directives/style-map';
import { booleanConverter } from './common/converters';
import { TeComponent } from './common/te-component';
import { theme } from './common/theme';
import { TRANSITION_DURATION_FAST } from './common/transition';

/**
 * Text Component
 *
 * @slot - Default slot for text
 */
@customElement('te-text')
export class TeText extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-text {
        margin: 0;
        padding: 0;
        transition: color ${TRANSITION_DURATION_FAST}ms ease-in-out;
      }
    `,
  ];

  /** Font Size */
  @property({ type: Number }) size: 10 | 12 | 14 | 16 | 24 | 32 = 14;
  /** Font weight */
  @property({ type: Number }) weight: 400 | 500 | 700 = 400;
  /** Font Color */
  @property({ type: String }) color = theme.colors.text.toString();
  /** Text alignment inside component */
  @property({ type: String }) align: 'initial' | 'left' | 'right' | 'center' = 'initial';
  // TODO: Do we need to allow display property at all? Initial has bug with line-heights, flex seems to be working.
  /** Display mode for text */
  @property({ type: String }) display: 'flex' | 'block' = 'flex';
  /** Text component should truncate */
  @property({ type: Boolean, converter: booleanConverter }) truncate = false;
  /** Truncate text to specific amount of lines */
  @property({ type: Number }) lineClamp = 0;
  /** Text is italic */
  @property({ type: Boolean }) italic = false;

  getStyle() {
    let styles: StyleInfo = {
      fontSize: `${this.size}px`,
      fontWeight: `${this.weight}`,
      color: `${this.color}`,
      lineHeight: `${this.size + 4}px`,
      textAlign: `${this.align}`,
      display: `${this.display}`,
      fontStyle: `${this.italic ? 'italic' : 'normal'}`,
    };
    if (this.truncate) {
      let lineClampStyle = {};
      const truncateStyle: StyleInfo = {
        whiteSpace: `${this.lineClamp ? 'unset' : 'nowrap'}`,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      };
      if (this.lineClamp) {
        lineClampStyle = {
          display: '-webkit-box',
          '-webkit-line-clamp': this.lineClamp,
          '-webkit-box-orient': 'vertical',
          wordBreak: 'none',
        };
      }
      styles = { ...styles, ...truncateStyle, ...lineClampStyle };
    }
    return styles;
  }

  render() {
    return html`
      <span class="te-text" style=${styleMap(this.getStyle())} @click=${this.handleClick}>
        <slot></slot>
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-text': TeText;
  }
}
